import { useEffect, useRef, useState } from 'react';
import { Plus, Check, X } from 'lucide-react';
import { apiClient } from '@/api/client';

interface Sticker {
  id: string;
  packId: string;
  url: string;
  isAnimated: boolean;
  mimeType: string;
  order: number;
}
interface StickerPack {
  id: string;
  name: string;
  isPublic: boolean;
  coverUrl: string | null;
  order: number;
  stickers?: Sticker[];
  _count?: { stickers: number };
}

const apiBase = ((import.meta as any).env?.VITE_API_URL ?? '').replace(/\/$/, '');
export function stickerSrc(url: string): string {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) return `${apiBase}${url}`;
  return url;
}

interface Props {
  onPick: (sticker: Sticker) => void;
  onClose: () => void;
}

export default function StickerPicker({ onPick, onClose }: Props) {
  const [tab, setTab] = useState<'mine' | 'public'>('mine');
  const [myPacks, setMyPacks] = useState<StickerPack[]>([]);
  const [publicPacks, setPublicPacks] = useState<StickerPack[]>([]);
  const [activePackId, setActivePackId] = useState<string | null>(null);
  const [activeStickers, setActiveStickers] = useState<Sticker[]>([]);
  const ref = useRef<HTMLDivElement>(null);

  const load = async () => {
    const [mine, pub] = await Promise.all([
      apiClient.get<StickerPack[]>('/api/stickers/me'),
      apiClient.get<StickerPack[]>('/api/stickers/packs'),
    ]);
    setMyPacks(mine.data);
    setPublicPacks(pub.data);
    // auto-select first available pack
    const first = mine.data[0] ?? pub.data[0];
    if (first) setActivePackId(first.id);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!activePackId) { setActiveStickers([]); return; }
    apiClient
      .get<StickerPack>(`/api/stickers/packs/${activePackId}`)
      .then((r) => setActiveStickers(r.data.stickers ?? []))
      .catch(() => setActiveStickers([]));
  }, [activePackId]);

  // Close on outside click / Escape
  useEffect(() => {
    const mouseHandler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const keyHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', mouseHandler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', mouseHandler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [onClose]);

  const myIds = new Set(myPacks.map((p) => p.id));
  const list = tab === 'mine' ? myPacks : publicPacks;
  const activeName = list.find((p) => p.id === activePackId)?.name
    ?? publicPacks.find((p) => p.id === activePackId)?.name
    ?? '';

  const addPack = async (id: string) => {
    await apiClient.post(`/api/stickers/me/${id}`);
    await load();
  };
  const removePack = async (id: string) => {
    await apiClient.delete(`/api/stickers/me/${id}`);
    await load();
  };

  return (
    <div
      ref={ref}
      className="absolute bottom-14 left-0 z-40 w-[360px] max-h-[400px] bg-surface-900 border border-surface-700 rounded-xl shadow-2xl flex flex-col"
    >
      <div className="flex border-b border-surface-800">
        <button
          onClick={() => setTab('mine')}
          className={`flex-1 py-2 text-sm ${tab === 'mine' ? 'text-brand-400 border-b-2 border-brand-500' : 'text-surface-400 hover:text-surface-200'}`}
        >
          Мои паки
        </button>
        <button
          onClick={() => setTab('public')}
          className={`flex-1 py-2 text-sm ${tab === 'public' ? 'text-brand-400 border-b-2 border-brand-500' : 'text-surface-400 hover:text-surface-200'}`}
        >
          Все публичные
        </button>
      </div>

      <div className="flex gap-1.5 p-2.5 overflow-x-auto border-b border-surface-800 min-h-[64px]">
        {list.length === 0 ? (
          <p className="text-surface-500 text-xs p-2">
            {tab === 'mine' ? 'Нет подключённых паков' : 'Публичных паков пока нет'}
          </p>
        ) : (
          list.map((p) => (
            <button
              key={p.id}
              onClick={() => setActivePackId(p.id)}
              className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center border transition ${activePackId === p.id ? 'bg-surface-800 border-brand-500' : 'bg-surface-800 border-surface-700 hover:border-surface-600'}`}
              title={p.name}
            >
              {p.coverUrl ? (
                <img src={stickerSrc(p.coverUrl)} alt="" className="w-9 h-9 object-contain" />
              ) : (
                <span className="text-xs text-surface-400 truncate max-w-[44px]">{p.name.slice(0, 3)}</span>
              )}
            </button>
          ))
        )}
      </div>

      {tab === 'public' && activePackId && (
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-surface-800 text-xs">
          <span className="text-surface-300 truncate">{activeName}</span>
          {myIds.has(activePackId) ? (
            <button onClick={() => removePack(activePackId)} className="text-red-400 hover:text-red-300 flex items-center gap-1">
              <X size={12} /> Отключить
            </button>
          ) : (
            <button onClick={() => addPack(activePackId)} className="text-brand-400 hover:text-brand-300 flex items-center gap-1">
              <Plus size={12} /> Подключить
            </button>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-2">
        {activeStickers.length === 0 ? (
          <p className="text-surface-500 text-xs text-center py-6">Нет стикеров в этом паке</p>
        ) : (
          <div className="grid grid-cols-5 gap-1.5">
            {activeStickers.map((st) => (
              <button
                key={st.id}
                onClick={() => onPick(st)}
                className="aspect-square bg-surface-800 hover:bg-surface-700 rounded-lg flex items-center justify-center p-1 transition-colors"
                title={st.isAnimated ? 'Анимированный стикер' : 'Стикер'}
              >
                {st.mimeType === 'application/json' ? (
                  <span className="text-[9px] text-surface-400">Lottie</span>
                ) : (
                  <img src={stickerSrc(st.url)} alt="" className="max-w-full max-h-full object-contain" loading="lazy" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
